(() => {
  function setupMintScene(root) {
    const scene = root.querySelector('.pdp-exp-scene--mint');
    if (!scene || !window.gsap || !window.ScrollTrigger) return;

    const { gsap, ScrollTrigger } = window;
    gsap.registerPlugin(ScrollTrigger);
    const background = scene.querySelector('.pdp-exp-scene-bg');
    const can = scene.querySelector('.pdp-exp-scene-can');
    const shadow = scene.querySelector('.pdp-exp-scene-shadow');
    const kicker = scene.querySelector('.pdp-exp-kicker');
    const titleLines = [...scene.querySelectorAll('.pdp-exp-title-text')];
    const stroke = scene.querySelector('.pdp-exp-stroke');
    const intro = scene.querySelector('.pdp-exp-scene-content > p:last-child');
    const signature = scene.querySelector('.pdp-exp-script');
    if (!can || titleLines.length !== 3) return;

    const media = gsap.matchMedia();
    media.add({
      desktop: '(min-width: 701px) and (prefers-reduced-motion: no-preference)',
      mobile: '(max-width: 700px) and (prefers-reduced-motion: no-preference)'
    }, context => {
      const compact = context.conditions.mobile;
      const targets = [background, can, shadow, kicker, ...titleLines, stroke, intro, signature].filter(Boolean);
      let lastSceneTime = 0;
      let vibration;
      const vibrateCan = () => {
        vibration?.kill();
        vibration = gsap.timeline({ defaults: { ease: 'sine.inOut' } })
          .to(can, { x: compact ? -2 : -4, rotation: -.8, duration: .07 })
          .to(can, { x: compact ? 2 : 3.5, rotation: .7, duration: .09 })
          .to(can, { x: compact ? -1.5 : -2.5, rotation: -.5, duration: .09 })
          .to(can, { x: compact ? 1 : 1.6, rotation: .3, duration: .1 })
          .to(can, { x: 0, rotation: 0, duration: .16, ease: 'power2.out' });
      };
      const timeline = gsap.timeline({
        defaults: { ease: 'none' },
        scrollTrigger: {
          trigger: scene,
          start: 'top 98%',
          end: 'top 58%',
          scrub: true,
          invalidateOnRefresh: true
        }
      });

      if (!compact && background) {
        timeline.fromTo(background, { y: 5, scale: 1.012 }, { y: 0, scale: 1, duration: 1.05 }, 0);
      }
      timeline
        .fromTo(can, { y: compact ? 36 : 80, scale: .95, opacity: 0 }, { y: 0, scale: 1, opacity: 1, duration: .6 }, 0)
        .fromTo(shadow, { opacity: 0, scale: .65 }, { opacity: 1, scale: 1, duration: .34 }, .22)
        .fromTo(kicker, { autoAlpha: 0, y: 9 }, { autoAlpha: 1, y: 0, duration: .13 }, .47);

      titleLines.forEach((line, index) => {
        timeline.fromTo(line, { yPercent: 145 }, { yPercent: 0, duration: .19 }, .53 + index * .09);
      });
      timeline
        .fromTo(stroke, { scaleX: 0, transformOrigin: 'left center' }, { scaleX: 1, duration: .15 }, .74)
        .fromTo(intro, { autoAlpha: 0, y: 11 }, { autoAlpha: 1, y: 0, duration: .19 }, .78)
        .fromTo(signature, { autoAlpha: 0, y: 10 }, { autoAlpha: 1, y: 0, duration: .18 }, .88);
      timeline.eventCallback('onUpdate', () => {
        const currentTime = timeline.time();
        if (currentTime >= .6 && lastSceneTime < .6) vibrateCan();
        lastSceneTime = currentTime;
      });

      return () => {
        vibration?.kill();
        timeline.scrollTrigger?.kill();
        timeline.kill();
        gsap.set(targets, { clearProps: 'transform,opacity,visibility' });
      };
    });
    root.mintMotionCleanup = () => media.revert();
  }

  function setupMintIngredients(root) {
    const section = root.querySelector('.pdp-exp-ingredients--mint');
    if (!section || !window.gsap || !window.ScrollTrigger) return;

    const { gsap, ScrollTrigger } = window;
    gsap.registerPlugin(ScrollTrigger);
    const media = gsap.matchMedia();
    media.add('(prefers-reduced-motion: no-preference)', () => {
      const kicker = section.querySelector('.pdp-exp-kicker');
      const titleLines = [...section.querySelectorAll('.pdp-exp-ingredients-title-line')];
      const stroke = section.querySelector('.pdp-exp-stroke');
      const description = section.querySelector('.pdp-exp-section-intro > p:not(.pdp-exp-kicker)');
      const cta = section.querySelector('.pdp-exp-outline');
      const cards = [...section.querySelectorAll('.pdp-exp-ingredient')];
      const mobile = window.matchMedia('(max-width: 700px)').matches;
      if (titleLines.length !== 2 || cards.length !== 4) return;

      const timeline = gsap.timeline({ paused: true, defaults: { ease: 'power2.out' } });
      timeline
        .fromTo(kicker, { opacity: 0, y: 9 }, { opacity: 1, y: 0, duration: .24 }, 0)
        .fromTo(titleLines[0], { yPercent: 120 }, { yPercent: 0, duration: .38 }, .13)
        .fromTo(titleLines[1], { yPercent: 120 }, { yPercent: 0, duration: .38 }, .23)
        .fromTo(stroke, { scaleX: 0, transformOrigin: 'left center' }, { scaleX: 1, duration: .28 }, .48)
        .fromTo(description, { opacity: 0, y: 10 }, { opacity: 1, y: 0, duration: .3 }, .56);

      cards.forEach((card, index) => {
        const art = card.querySelector('.pdp-exp-ingredient-art');
        const image = art.querySelector('img');
        const heading = card.querySelector('h3');
        const copy = card.querySelector('p');
        const start = .76 + index * .14;
        timeline
          .fromTo(art, { opacity: 0, y: mobile ? 18 : 25, scale: .92 }, { opacity: 1, y: 0, scale: 1, duration: .46 }, start)
          .fromTo([heading, copy], { opacity: 0, y: 9 }, { opacity: 1, y: 0, duration: .3, stagger: .08 }, start + .12);
        if (index > 0) {
          timeline.fromTo(card, { '--pdp-separator-scale': 0 }, { '--pdp-separator-scale': 1, duration: .32, ease: 'power1.out' }, start + .2);
        }

        if (index === 0) {
          const flakes = [...art.querySelectorAll('.pdp-exp-mate-flake')];
          flakes.forEach((flake, flakeIndex) => {
            timeline
              .fromTo(flake, { opacity: 0, y: 2, x: 0 }, { opacity: .68, y: mobile ? -4 : -8, x: flakeIndex ? 4 : -3, duration: .32 }, start + .32 + flakeIndex * .07)
              .to(flake, { opacity: 0, y: mobile ? -8 : -15, duration: .35 }, start + .64 + flakeIndex * .07);
          });
        } else if (index === 1) {
          timeline
            .to(image, { rotation: -1.2, transformOrigin: '50% 85%', duration: .22, ease: 'sine.inOut' }, start + .37)
            .to(image, { rotation: .65, duration: .24, ease: 'sine.inOut' }, start + .59)
            .to(image, { rotation: 0, duration: .22, ease: 'sine.out' }, start + .83);
        } else if (index === 2) {
          const glint = art.querySelector('.pdp-exp-lemon-glint');
          timeline
            .fromTo(glint, { opacity: 0, backgroundPositionX: '0%' }, { opacity: .7, backgroundPositionX: '100%', duration: .6, ease: 'sine.inOut' }, start + .36)
            .to(glint, { opacity: 0, duration: .16 }, start + .92);
        } else {
          art.querySelectorAll('.pdp-exp-rising-bubble').forEach((bubble, bubbleIndex) => {
            const offset = start + .35 + bubbleIndex * .09;
            timeline
              .fromTo(bubble, { opacity: 0, y: 8 }, { opacity: .7, y: mobile ? -12 : -21, duration: .52, ease: 'sine.out' }, offset)
              .to(bubble, { opacity: 0, duration: .22 }, offset + .48);
          });
        }
      });
      timeline.fromTo(cta, { opacity: 0, y: 10 }, { opacity: 1, y: 0, duration: .32 }, 1.73);
      timeline.progress(0).pause();

      let played = false;
      const playOnce = () => {
        if (played) return;
        played = true;
        timeline.play();
      };
      const trigger = ScrollTrigger.create({
        trigger: section,
        start: 'top 95%',
        onEnter: playOnce,
        onEnterBack: playOnce,
        onLeave: () => timeline.progress(1)
      });
      return () => {
        trigger.kill();
        timeline.kill();
        gsap.set([kicker, ...titleLines, stroke, description, cta, ...cards, ...section.querySelectorAll('.pdp-exp-ingredient-art, .pdp-exp-ingredient h3, .pdp-exp-ingredient p, .pdp-exp-ingredient-art img, .pdp-exp-mate-flake, .pdp-exp-lemon-glint, .pdp-exp-rising-bubble')], { clearProps: 'opacity,transform,background-position-x,--pdp-separator-scale' });
      };
    });
    root.ingredientsMotionCleanup = () => media.revert();
  }

  function setup(root) {
    if (!root || root.dataset.ready) return;
    root.dataset.ready = 'true';

    const product = document.querySelector('[data-pdp]');
    const form = product?.querySelector('form.pdp-form');
    const add = root.querySelector('[data-pdp-experience-add]');
    const ingredientsLink = root.querySelector('[data-pdp-open-ingredients]');
    const currency = JSON.parse(product?.querySelector('[data-pdp-data]')?.textContent || '{}').currency || 'EUR';
    const money = amount => new Intl.NumberFormat(window.BrioI18n.locale, { style: 'currency', currency }).format(amount / 100);

    add?.addEventListener('click', () => {
      if (!form) return;
      const submit = form.querySelector('[type=submit]');
      if (submit && !submit.disabled) form.requestSubmit(submit);
    });

    ingredientsLink?.addEventListener('click', () => {
      product?.querySelector('.pdp-details details')?.setAttribute('open', '');
    });

    const updateProduct = event => {
      const { variant, price, units } = event.detail;
      const unitsLabel = root.querySelector('[data-pdp-exp-units]');
      const priceLabel = root.querySelector('[data-pdp-exp-price]');
      const unitPriceLabel = root.querySelector('[data-pdp-exp-unit-price]');
      if (unitsLabel) unitsLabel.textContent = String(units) + ' ' + window.BrioI18n.t('s_1e4cf986bc1b_html');
      if (priceLabel) priceLabel.textContent = money(price);
      if (unitPriceLabel) unitPriceLabel.textContent = money(price / units);
      if (add) add.disabled = !variant.available;
    };
    document.addEventListener('brio:pdp:update', updateProduct);

    root.experienceCleanup = () => {
      document.removeEventListener('brio:pdp:update', updateProduct);
      root.mintMotionCleanup?.();
      root.ingredientsMotionCleanup?.();
    };

    setupMintScene(root);
    setupMintIngredients(root);
  }

  document.querySelectorAll('[data-pdp-experience]').forEach(setup);
  document.addEventListener('shopify:section:load', event => setup(event.target.querySelector('[data-pdp-experience]')));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelector('[data-pdp-experience]')?.experienceCleanup?.();
  });
})();
